import { tintHex } from './colour.js';
import { palette, type PaletteName } from './palette.js';

export type RoleValue =
  | { kind: 'palette'; name: PaletteName }
  | { kind: 'role'; token: string }
  | { kind: 'tint'; name: PaletteName; percent: number };

export type Theme = Record<string, RoleValue>;

export const fromPalette = (name: PaletteName): RoleValue => ({ kind: 'palette', name });
export const fromRole = (token: string): RoleValue => ({ kind: 'role', token });
export const tintOf = (name: PaletteName, percent: number): RoleValue => ({ kind: 'tint', name, percent });

export const paletteProperty = (name: string) => `--ns-theme-${name}`;

const declaration = (property: string, value: string) => `  ${property}: ${value};`;

function cssValue(value: RoleValue): string {
  switch (value.kind) {
    case 'palette':
      return `var(${paletteProperty(value.name)})`;
    case 'role':
      return `var(${value.token})`;
    case 'tint':
      return `color-mix(in srgb, var(${paletteProperty(value.name)}) ${value.percent}%, var(${paletteProperty('white')}))`;
  }
}

export function paletteDeclarations(): string {
  return Object.entries(palette)
    .map(([name, hex]) => declaration(paletteProperty(name), hex))
    .join('\n');
}

export function themeDeclarations(theme: Theme): string {
  return Object.entries(theme)
    .map(([token, value]) => declaration(token, cssValue(value)))
    .join('\n');
}

export function resolveRole(theme: Theme, token: string, chain: string[] = []): string {
  const value = theme[token];
  if (!value) throw new Error(`Unknown role ${token}`);
  if (chain.includes(token)) throw new Error(`Role cycle: ${[...chain, token].join(' -> ')}`);

  switch (value.kind) {
    case 'palette':
      return palette[value.name];
    case 'tint':
      return tintHex(palette[value.name], value.percent);
    case 'role':
      return resolveRole(theme, value.token, [...chain, token]);
  }
}

export function paletteNamesUsed(theme: Theme): PaletteName[] {
  const used = Object.keys(theme)
    .map((token) => theme[token])
    .filter((value): value is Exclude<RoleValue, { kind: 'role' }> => value.kind !== 'role')
    .map((value) => value.name);
  return (Object.keys(palette) as PaletteName[]).filter((name) => used.includes(name));
}
